import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Eye,
  X,
  Play,
  Pause,
  SkipBack,
  SkipForward,
  Sparkles,
  Loader2,
  Video,
  Edit2,
  ChevronDown,
  ChevronRight,
  GripVertical,
  Copy,
  Trash2,
  Type,
  AlertTriangle,
} from 'lucide-react';
import type { AIPreview, ProjectFile } from '@/types/video-enhancer';
import type { ProjectClip } from '@/components/video-editor/TimelineSequence';

interface AIEditPreviewProps {
  aiPreview: AIPreview | null;
  isLoadingPreview: boolean;
  files: ProjectFile[];
  clips: ProjectClip[];
  onClose: () => void;
  onEditInTimeline: () => void;
  onRenderFromPreview: () => void;
  onReorderClips: (fromIndex: number, toIndex: number) => void;
  onDuplicateClip: (index: number) => void;
  onDeleteClip: (index: number) => void;
  onUpdateClipTrim: (index: number, newTrim: number, newLength: number) => void;
  onUpdateTextOverlay: (index: number, newText: string) => void;
  onUpdateTextOverlayTiming: (index: number, newStart: number, newLength: number) => void;
  onDeleteTextOverlay: (index: number) => void;
}

export const AIEditPreview: React.FC<AIEditPreviewProps> = ({
  aiPreview,
  isLoadingPreview,
  files,
  clips,
  onClose,
  onEditInTimeline,
  onRenderFromPreview,
  onReorderClips,
  onDuplicateClip,
  onDeleteClip,
  onUpdateClipTrim,
  onUpdateTextOverlay,
  onUpdateTextOverlayTiming,
  onDeleteTextOverlay,
}) => {
  const [currentClip, setCurrentClip] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [detailsExpanded, setDetailsExpanded] = useState(false);
  const [draggedClipIndex, setDraggedClipIndex] = useState<number | null>(null);
  const [dragOverClipIndex, setDragOverClipIndex] = useState<number | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  const handleClose = () => {
    if (videoRef.current) videoRef.current.pause();
    setIsPlaying(false);
    onClose();
  };

  const videoStepsCount = aiPreview?.steps.filter(s => s.type === 'video' && s.asset?.src).length || 0;
  useEffect(() => {
    if (videoStepsCount > 0 && currentClip >= videoStepsCount) {
      setCurrentClip(Math.max(0, videoStepsCount - 1));
    }
  }, [videoStepsCount, currentClip]);

  return (
    <div className="panel mt-4 animate-in slide-in-from-top-2 duration-300">
      {/* Header */}
      <div className="panel-header flex items-center justify-between">
        <h3 className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider">
          <Eye className="h-3.5 w-3.5 text-primary" />
          AI Edit Preview
        </h3>
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={handleClose}
          className="h-6 w-6 text-muted-foreground hover:text-foreground"
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>

      <div className="p-4">
        {isLoadingPreview ? (
          <div className="flex flex-col items-center justify-center py-12 gap-3">
            <div className="relative">
              <div className="h-12 w-12 rounded-full border-2 border-muted" />
              <Loader2 className="absolute inset-0 h-12 w-12 animate-spin text-primary" />
            </div>
            <p className="text-sm text-muted-foreground">
              AI is analyzing your clips...
            </p>
            <p className="text-xs text-muted-foreground/70">
              This may take 10-30 seconds
            </p>
          </div>
        ) : aiPreview ? (
          <div className="space-y-4">
            {(() => {
              const videoSteps = aiPreview.steps.filter(s => s.type === 'video' && s.asset?.src);
              const currentStep = videoSteps[currentClip] || videoSteps[0];

              return videoSteps.length > 0 ? (
                <>
                  {/* Video Player */}
                  <div className="flex justify-center">
                    <div className="relative aspect-video w-full max-w-[720px] overflow-hidden rounded-lg bg-[hsl(var(--timeline))] ring-1 ring-border">
                      <video
                        ref={videoRef}
                        className="h-full w-full object-contain"
                        src={currentStep?.asset?.src || undefined}
                        onTimeUpdate={() => {
                          if (videoRef.current && isPlaying) {
                            const trimStart = (currentStep?.asset as Record<string, unknown>)?.trim as number || 0;
                            const clipLength = currentStep?.timing?.length || 5;
                            const clipEndTime = trimStart + clipLength;
                            const ct = videoRef.current.currentTime;
                            if (ct >= clipEndTime) {
                              if (currentClip < videoSteps.length - 1) {
                                setCurrentClip(currentClip + 1);
                              } else {
                                videoRef.current.pause();
                                setIsPlaying(false);
                                setCurrentClip(0);
                              }
                            }
                          }
                        }}
                        onLoadedMetadata={() => {
                          if (videoRef.current) {
                            const trimStart = (currentStep?.asset as Record<string, unknown>)?.trim as number || 0;
                            videoRef.current.currentTime = trimStart;
                            if (isPlaying) {
                              videoRef.current.play().catch(console.error);
                            }
                          }
                        }}
                        onEnded={() => {
                          if (currentClip < videoSteps.length - 1) {
                            setCurrentClip(currentClip + 1);
                          } else {
                            setIsPlaying(false);
                            setCurrentClip(0);
                          }
                        }}
                      />
                      {/* Overlay badges */}
                      <div className="absolute left-3 top-3 rounded bg-black/80 px-2 py-1 text-xs font-medium text-white backdrop-blur-sm">
                        Clip {currentClip + 1}/{videoSteps.length}
                      </div>
                      <div className="absolute bottom-3 left-3 rounded bg-black/80 px-2 py-1 text-[10px] font-mono text-white/80 backdrop-blur-sm">
                        {currentStep?.timing?.start || 0}s - {currentStep?.timing?.end || 0}s
                        {(currentStep?.asset as Record<string, unknown>)?.trim !== undefined && 
                         ((currentStep?.asset as Record<string, unknown>)?.trim as number) > 0 && (
                          <span className="ml-2 text-muted-foreground">
                            src: {((currentStep?.asset as Record<string, unknown>)?.trim as number).toFixed(1)}s
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Playback Controls */}
                  <div className="flex items-center justify-center gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      disabled={currentClip === 0}
                      onClick={() => setCurrentClip(Math.max(0, currentClip - 1))}
                      className="h-9 w-9 text-muted-foreground hover:text-foreground"
                    >
                      <SkipBack className="h-4 w-4" />
                    </Button>
                    <Button
                      size="icon"
                      onClick={() => {
                        if (isPlaying) {
                          videoRef.current?.pause();
                          setIsPlaying(false);
                        } else {
                          videoRef.current?.play().catch(console.error);
                          setIsPlaying(true);
                        }
                      }}
                      className="h-12 w-12 rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                    >
                      {isPlaying ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5 ml-0.5" />}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      disabled={currentClip === videoSteps.length - 1}
                      onClick={() => setCurrentClip(Math.min(videoSteps.length - 1, currentClip + 1))}
                      className="h-9 w-9 text-muted-foreground hover:text-foreground"
                    >
                      <SkipForward className="h-4 w-4" />
                    </Button>
                  </div>

                  {/* Clip Thumbnails */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <h4 className="text-xs font-medium text-muted-foreground">
                        Video Clips ({videoSteps.length})
                      </h4>
                      <span className="text-xs text-muted-foreground">
                        {aiPreview.summary.totalDuration}
                        {aiPreview.summary.audioTracks > 0 && (
                          <span className="ml-2">+ {aiPreview.summary.audioTracks} audio</span>
                        )}
                      </span>
                    </div>
                    <ScrollArea className="w-full">
                      <div className="flex gap-1.5 pb-2">
                        {videoSteps.map((step, index) => {
                          const srcUrl = step.asset?.src || '';
                          let thumbnail: string | undefined;

                          const matchingClip = clips.find(c => {
                            const file = files.find(f => f.id === c.sourceFileId);
                            return file?.s3Url && (srcUrl.includes(file.s3Key || '') || srcUrl === file.s3Url);
                          });
                          if (matchingClip?.thumbnailUrl && matchingClip.thumbnailUrl !== 'VIDEO_ICON') {
                            thumbnail = matchingClip.thumbnailUrl;
                          }

                          if (!thumbnail) {
                            const matchingFile = files.find(f =>
                              f.s3Url === srcUrl ||
                              (f.s3Key && srcUrl.includes(f.s3Key)) ||
                              srcUrl.includes(f.name)
                            );
                            if (matchingFile?.thumbnailUrl && matchingFile.thumbnailUrl !== 'VIDEO_ICON') {
                              thumbnail = matchingFile.thumbnailUrl;
                            }
                          }

                          return (
                            <button
                              key={index}
                              onClick={() => {
                                setCurrentClip(index);
                                if (videoRef.current) {
                                  const trimStart = (step.asset as Record<string, unknown>)?.trim as number || 0;
                                  videoRef.current.currentTime = trimStart;
                                }
                              }}
                              className={`group relative flex-shrink-0 w-20 overflow-hidden rounded transition-all ${
                                index === currentClip
                                  ? 'ring-2 ring-primary ring-offset-1 ring-offset-background'
                                  : 'ring-1 ring-border hover:ring-muted-foreground'
                              }`}
                            >
                              <div className="aspect-video bg-[hsl(var(--timeline))]">
                                {thumbnail && thumbnail !== 'VIDEO_ICON' ? (
                                  <img src={thumbnail} alt={`Clip ${index + 1}`} className="h-full w-full object-cover" />
                                ) : (
                                  <div className="flex h-full w-full items-center justify-center">
                                    <Video className="h-4 w-4 text-muted-foreground" />
                                  </div>
                                )}
                                <div className="absolute left-0.5 top-0.5 rounded bg-black/70 px-1 text-[9px] font-medium text-white">
                                  {index + 1}
                                </div>
                                <div className="absolute bottom-0.5 right-0.5 rounded bg-black/70 px-1 text-[9px] font-mono text-white/80">
                                  {step.timing?.length?.toFixed(1) || '?'}s
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </ScrollArea>
                  </div>

                  {/* AI Effects Summary */}
                  <div className="rounded-lg border border-border bg-[hsl(var(--panel))]">
                    <button
                      onClick={() => setDetailsExpanded(!detailsExpanded)}
                      className="flex w-full items-center justify-between p-3 text-left transition-colors hover:bg-muted/30"
                    >
                      <span className="flex items-center gap-2 text-xs font-medium">
                        <Edit2 className="h-3.5 w-3.5 text-primary" />
                        Edit Clips
                        {detailsExpanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {videoSteps.length} clips · {aiPreview.summary.totalDuration}
                      </span>
                    </button>

                    {/* Stats Grid */}
                    <div className="grid grid-cols-2 gap-2 border-t border-border p-3 md:grid-cols-4">
                      {(() => {
                        const clipsWithTrim = videoSteps.filter(s => (s.asset as Record<string, unknown>)?.trim !== undefined && ((s.asset as Record<string, unknown>)?.trim as number) > 0);
                        return clipsWithTrim.length > 0 && (
                          <div className="rounded bg-secondary/50 p-2">
                            <div className="text-xs font-medium">Trimmed</div>
                            <div className="text-[10px] text-muted-foreground">{clipsWithTrim.length} clips</div>
                          </div>
                        );
                      })()}
                      {(() => {
                        const textSteps = aiPreview.steps.filter(s => s.type === 'text' || s.asset?.type === 'text');
                        return textSteps.length > 0 && (
                          <div className="rounded bg-secondary/50 p-2">
                            <div className="text-xs font-medium">Text</div>
                            <div className="text-[10px] text-muted-foreground">{textSteps.length} overlays</div>
                          </div>
                        );
                      })()}
                      {(() => {
                        const withTransitions = aiPreview.steps.filter(s => (s as Record<string, unknown>).transition);
                        return withTransitions.length > 0 && (
                          <div className="rounded bg-secondary/50 p-2">
                            <div className="text-xs font-medium">Transitions</div>
                            <div className="text-[10px] text-muted-foreground">{withTransitions.length} effects</div>
                          </div>
                        );
                      })()}
                      {aiPreview.summary.audioTracks > 0 && (
                        <div className="rounded bg-secondary/50 p-2">
                          <div className="text-xs font-medium">Audio</div>
                          <div className="text-[10px] text-muted-foreground">{aiPreview.summary.audioTracks} tracks</div>
                        </div>
                      )}
                      <div className="rounded bg-secondary/50 p-2">
                        <div className="text-xs font-medium">Duration</div>
                        <div className="text-[10px] text-muted-foreground">{aiPreview.summary.totalDuration}</div>
                      </div>
                    </div>

                    {/* Expanded Details */}
                    {detailsExpanded && (
                      <div className="border-t border-border p-3">
                        <p className="mb-3 text-[10px] text-muted-foreground">
                          Drag to reorder · Click name to preview · Duplicate to use multiple parts
                        </p>
                        <div className="max-h-64 space-y-1 overflow-y-auto">
                          {videoSteps.map((step, idx) => {
                            const srcUrl = step.asset?.src || '';
                            const fileName = srcUrl.split('/').pop()?.split('?')[0] || `Clip ${idx + 1}`;
                            const isCurrentClip = idx === currentClip;
                            const trimStart = (step.asset as Record<string, unknown>)?.trim as number || 0;
                            const clipLength = step.timing?.length || 5;
                            const isDragging = draggedClipIndex === idx;
                            const isDragOver = dragOverClipIndex === idx && draggedClipIndex !== idx;

                            return (
                              <div
                                key={idx}
                                draggable
                                onDragStart={(e) => {
                                  setDraggedClipIndex(idx);
                                  e.dataTransfer.effectAllowed = 'move';
                                }}
                                onDragEnd={() => {
                                  setDraggedClipIndex(null);
                                  setDragOverClipIndex(null);
                                }}
                                onDragOver={(e) => {
                                  e.preventDefault();
                                  setDragOverClipIndex(idx);
                                }}
                                onDragLeave={() => setDragOverClipIndex(null)}
                                onDrop={(e) => {
                                  e.preventDefault();
                                  if (draggedClipIndex !== null && draggedClipIndex !== idx) {
                                    onReorderClips(draggedClipIndex, idx);
                                  }
                                  setDraggedClipIndex(null);
                                  setDragOverClipIndex(null);
                                }}
                                className={`cursor-grab rounded border p-2 transition-all active:cursor-grabbing ${
                                  isDragging
                                    ? 'border-dashed border-muted-foreground opacity-50'
                                    : isDragOver
                                    ? 'border-2 border-primary bg-primary/5'
                                    : isCurrentClip
                                    ? 'border-primary/50 bg-primary/5'
                                    : 'border-border bg-secondary/30 hover:border-muted-foreground'
                                }`}
                              >
                                <div className="mb-2 flex items-center gap-2">
                                  <GripVertical className="h-4 w-4 cursor-grab text-muted-foreground hover:text-foreground" />
                                  <span className={`font-mono text-[10px] font-bold ${isCurrentClip ? 'text-primary' : 'text-muted-foreground'}`}>
                                    #{idx + 1}
                                  </span>
                                  <span
                                    className="flex-1 cursor-pointer truncate text-xs font-medium hover:text-primary"
                                    onClick={() => {
                                      setCurrentClip(idx);
                                      if (videoRef.current) {
                                        videoRef.current.currentTime = trimStart;
                                      }
                                    }}
                                  >
                                    {fileName.length > 20 ? fileName.slice(0, 20) + '...' : fileName}
                                  </span>
                                  <div className="flex items-center gap-0.5">
                                    <button
                                      onClick={() => onDuplicateClip(idx)}
                                      className="rounded p-1 text-primary hover:bg-primary/10"
                                      title="Duplicate clip"
                                    >
                                      <Copy className="h-3 w-3" />
                                    </button>
                                    <button
                                      onClick={() => {
                                        if (confirm('Delete this clip from the edit?')) {
                                          onDeleteClip(idx);
                                        }
                                      }}
                                      className="rounded p-1 text-destructive hover:bg-destructive/10"
                                      title="Delete clip"
                                    >
                                      <Trash2 className="h-3 w-3" />
                                    </button>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2 text-[10px]">
                                  <span className="w-6 text-muted-foreground">In:</span>
                                  <input
                                    type="text"
                                    inputMode="decimal"
                                    defaultValue={trimStart.toFixed(1)}
                                    key={`in-${idx}-${trimStart.toFixed(1)}`}
                                    onBlur={(e) => {
                                      const newTrim = parseFloat(e.target.value) || 0;
                                      if (newTrim !== trimStart) {
                                        onUpdateClipTrim(idx, newTrim, clipLength);
                                        if (idx === currentClip && videoRef.current) {
                                          videoRef.current.currentTime = newTrim;
                                        }
                                      }
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') e.currentTarget.blur();
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                    onMouseDown={(e) => e.stopPropagation()}
                                    className="w-14 rounded border border-border bg-input px-1.5 py-0.5 text-xs"
                                  />
                                  <span className="text-muted-foreground">s</span>
                                  <span className="w-8 text-muted-foreground">Len:</span>
                                  <input
                                    type="text"
                                    inputMode="decimal"
                                    defaultValue={clipLength.toFixed(1)}
                                    key={`len-${idx}-${clipLength.toFixed(1)}`}
                                    onBlur={(e) => {
                                      const newLength = parseFloat(e.target.value) || 1;
                                      if (newLength !== clipLength) {
                                        onUpdateClipTrim(idx, trimStart, newLength);
                                      }
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') e.currentTarget.blur();
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                    onMouseDown={(e) => e.stopPropagation()}
                                    className="w-14 rounded border border-border bg-input px-1.5 py-0.5 text-xs"
                                  />
                                  <span className="text-muted-foreground">s</span>
                                  <span className="ml-auto text-[9px] text-muted-foreground">
                                    {(trimStart + clipLength).toFixed(1)}s
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        {/* Text Overlays */}
                        {(() => {
                          const textSteps = aiPreview.steps.filter(s => s.type === 'text' || s.asset?.type === 'text');
                          const totalVideoDuration = videoSteps.reduce((sum, s) => sum + (s.timing?.length || 0), 0);

                          const getOverlayClipInfo = (start: number) => {
                            let cumulative = 0;
                            for (let i = 0; i < videoSteps.length; i++) {
                              const clipLen = videoSteps[i].timing?.length || 0;
                              if (start >= cumulative && start < cumulative + clipLen) {
                                return { clipIndex: i, clipName: videoSteps[i].asset?.src?.split('/').pop()?.split('?')[0] || `Clip ${i + 1}` };
                              }
                              cumulative += clipLen;
                            }
                            return null;
                          };

                          return textSteps.length > 0 && (
                            <div className="mt-3 border-t border-border pt-3">
                              <h5 className="mb-2 flex items-center gap-2 text-xs font-medium text-primary">
                                <Type className="h-3 w-3" />
                                Text Overlays
                              </h5>
                              {textSteps.map((step, idx) => {
                                const overlayStart = step.timing?.start || 0;
                                const overlayLength = step.timing?.length || 3;
                                const clipInfo = getOverlayClipInfo(overlayStart);
                                const isOverBlack = !clipInfo || overlayStart >= totalVideoDuration;

                                return (
                                  <div
                                    key={idx}
                                    className={`mb-1.5 rounded border p-2 text-xs ${
                                      isOverBlack
                                        ? 'border-warning/50 bg-warning/10'
                                        : 'border-border bg-secondary/30'
                                    }`}
                                  >
                                    {isOverBlack && (
                                      <div className="mb-2 flex items-center gap-1 text-[10px] text-warning">
                                        <AlertTriangle className="h-3 w-3" />
                                        <span>Text appears over black - adjust timing</span>
                                      </div>
                                    )}
                                    {clipInfo && (
                                      <div className="mb-1.5 text-[10px] text-muted-foreground">
                                        Over: Clip #{clipInfo.clipIndex + 1} ({clipInfo.clipName.slice(0, 15)}...)
                                      </div>
                                    )}
                                    <input
                                      type="text"
                                      value={step.asset?.text || ''}
                                      onChange={(e) => onUpdateTextOverlay(idx, e.target.value)}
                                      className="mb-2 w-full rounded border border-border bg-input px-2 py-1 text-xs"
                                      placeholder="Enter text..."
                                    />
                                    <div className="flex items-center gap-2 text-[10px]">
                                      <span className="text-muted-foreground">At:</span>
                                      <input
                                        type="text"
                                        inputMode="decimal"
                                        defaultValue={overlayStart.toFixed(1)}
                                        key={`text-start-${idx}-${overlayStart.toFixed(1)}`}
                                        onBlur={(e) => {
                                          const newStart = parseFloat(e.target.value) || 0;
                                          if (newStart !== overlayStart) {
                                            onUpdateTextOverlayTiming(idx, newStart, overlayLength);
                                          }
                                        }}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') e.currentTarget.blur();
                                        }}
                                        className="w-12 rounded border border-border bg-input px-1 py-0.5 text-xs"
                                      />
                                      <span className="text-muted-foreground">s for</span>
                                      <input
                                        type="text"
                                        inputMode="decimal"
                                        defaultValue={overlayLength.toFixed(1)}
                                        key={`text-len-${idx}-${overlayLength.toFixed(1)}`}
                                        onBlur={(e) => {
                                          const newLen = parseFloat(e.target.value) || 1;
                                          if (newLen !== overlayLength) {
                                            onUpdateTextOverlayTiming(idx, overlayStart, newLen);
                                          }
                                        }}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') e.currentTarget.blur();
                                        }}
                                        className="w-12 rounded border border-border bg-input px-1 py-0.5 text-xs"
                                      />
                                      <span className="text-muted-foreground">s</span>
                                      <div className="ml-auto flex gap-1">
                                        {videoSteps.slice(0, 3).map((_, clipIdx) => {
                                          const clipStart = videoSteps.slice(0, clipIdx).reduce((sum, s) => sum + (s.timing?.length || 0), 0);
                                          return (
                                            <button
                                              key={clipIdx}
                                              onClick={() => onUpdateTextOverlayTiming(idx, clipStart, overlayLength)}
                                              className={`rounded px-1.5 py-0.5 text-[9px] transition-colors ${
                                                clipInfo?.clipIndex === clipIdx
                                                  ? 'bg-primary text-primary-foreground'
                                                  : 'bg-secondary hover:bg-muted'
                                              }`}
                                              title={`Move to Clip ${clipIdx + 1}`}
                                            >
                                              #{clipIdx + 1}
                                            </button>
                                          );
                                        })}
                                      </div>
                                      <button
                                        onClick={() => {
                                          if (confirm('Delete this text overlay?')) {
                                            onDeleteTextOverlay(idx);
                                          }
                                        }}
                                        className="rounded p-1 text-destructive hover:bg-destructive/10"
                                        title="Delete overlay"
                                      >
                                        <Trash2 className="h-3 w-3" />
                                      </button>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })()}
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center justify-between gap-2 border-t border-border pt-4">
                    <Button variant="ghost" onClick={handleClose} className="text-muted-foreground">
                      Cancel
                    </Button>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        onClick={() => {
                          if (videoRef.current) videoRef.current.pause();
                          setIsPlaying(false);
                          onEditInTimeline();
                        }}
                      >
                        <Edit2 className="mr-2 h-4 w-4" />
                        Edit in Timeline
                      </Button>
                      <Button
                        onClick={() => {
                          if (videoRef.current) videoRef.current.pause();
                          setIsPlaying(false);
                          onRenderFromPreview();
                        }}
                        className="bg-primary text-primary-foreground hover:bg-primary/90"
                      >
                        <Sparkles className="mr-2 h-4 w-4" />
                        Render This Edit
                      </Button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="py-12 text-center text-muted-foreground">
                  No video clips in this preview
                </div>
              );
            })()}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center gap-3 py-12">
            <p className="text-sm text-muted-foreground">
              No preview available. Click "Preview Timeline" to generate one.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
